import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Ticket,
  Search,
  Clock,
  CheckCircle,
  Pause,
  AlertTriangle,
  Building2,
  User,
  Eye,
  X,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Send,
  MessageSquare,
  Shield,
  Image,
  Users,
  Building,
  Mail,
  Loader2,
  Filter,
  Archive
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';
import { SuccessPopup } from '../../components/SuccessPopup';
import { openAttachment } from '../../utils/openAttachment';
import { BulkTicketModal, BulkSendsManager } from './BulkTicketModal';


const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  
  // Tab attivo: aperti/in lavorazione vs chiusi
  const [activeTab, setActiveTab] = useState('aperti');
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterResponsible, setFilterResponsible] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);
  
  // Dettaglio ticket
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Risposta
  const [replyContent, setReplyContent] = useState('');
  const [replyRecipients, setReplyRecipients] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  
  // Moderation logs
  const [showModLogs, setShowModLogs] = useState(false);
  const [moderationLogs, setModerationLogs] = useState([]);
  
  // Nuovo ticket da ufficio (form basato su immobile)
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newPropertySearch, setNewPropertySearch] = useState('');
  const [newSelectedProperty, setNewSelectedProperty] = useState(null);
  const [newPropertyInfo, setNewPropertyInfo] = useState(null);
  const [newTicketRecipients, setNewTicketRecipients] = useState({ responsabile: false, proprieta: false, partner: false });
  const [newTicketForm, setNewTicketForm] = useState({ subject: '', message: '', priority: 'normale', category: '' });
  const [newTicketAttachments, setNewTicketAttachments] = useState([]);
  const [sendingNewTicket, setSendingNewTicket] = useState(false);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [showBulkTicket, setShowBulkTicket] = useState(false);
  const [showBulkSends, setShowBulkSends] = useState(false);
  const [popup, setPopup] = useState({ open: false, title: '', message: '', type: 'success' });
  const propertyDropdownRef = useRef(null);
  
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('hw_token');

  // Categorie ticket
  const TICKET_CATEGORIES = {
    clima: { label: "Clima/Riscaldamento", color: "blue" },
    impianti: { label: "Impianti (Wifi, elettrico)", color: "blue" },
    comfort: { label: "Comfort (arredi, rumore)", color: "blue" },
    dotazioni: { label: "Dotazioni mancanti", color: "blue" },
    manutenzione: { label: "Manutenzione/Guasti", color: "blue" },
    checkin: { label: "Check-in/Check-out", color: "green" },
    pulizia: { label: "Pulizia alloggio", color: "green" },
    ospite: { label: "Problemi con ospite", color: "green" },
    pagamenti: { label: "Pagamenti/Fatture", color: "purple" },
    comunicazioni: { label: "Comunicazioni/Info", color: "purple" },
    sistema: { label: "Problemi sistema", color: "purple" },
    altro: { label: "Altro", color: "gray" }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketsRes, statsRes, propsRes, managersRes] = await Promise.all([
        fetch(`${API_URL}/api/tickets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/tickets/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/properties`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/property-managers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setTickets(ticketsData);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (propsRes.ok) {
        const propsData = await propsRes.json();
        setProperties(Array.isArray(propsData) ? propsData : propsData.items || []);
      }
      if (managersRes.ok) {
        const managersData = await managersRes.json();
        setResponsibles(Array.isArray(managersData) ? managersData : managersData.items || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
        setTicketMessages(data.messages);
        // Smart reply recipients dal backend
        if (data.ticket.suggested_reply_recipients) {
          setReplyRecipients(data.ticket.suggested_reply_recipients);
        } else {
          setReplyRecipients(['responsabile']);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore caricamento dettaglio');
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchModerationLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ticket-moderation-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setModerationLogs(await res.json());
        setShowModLogs(true);
      }
    } catch (error) {
      toast.error('Errore caricamento log');
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.slice(0, 5).forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini permesse');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Max 5MB per immagine');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setReplyAttachments(prev => [...prev.slice(0, 4), reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedTicket) return;
    if (replyRecipients.length === 0) {
      toast.error('Seleziona almeno un destinatario');
      return;
    }

    setSendingReply(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: replyContent,
          recipients: replyRecipients,
          attachments: replyAttachments
        })
      });

      const data = await res.json();

      if (res.ok) {
        setPopup({ open: true, title: 'Messaggio inviato!', message: 'La risposta è stata inviata al responsabile.', type: 'success' });
        setReplyContent('');
        setReplyAttachments([]);
        loadTicketDetail(selectedTicket.id);
        fetchData();
      } else {
        setPopup({ open: true, title: 'Errore', message: data.detail || 'Errore invio messaggio', type: 'error' });
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedTicket) return;

    try {
      const res = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        toast.success('Stato aggiornato');
        loadTicketDetail(selectedTicket.id);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const toggleRecipient = (recipient) => {
    setReplyRecipients(prev => 
      prev.includes(recipient)
        ? prev.filter(r => r !== recipient)
        : [...prev, recipient]
    );
  };

  // === NUOVO TICKET DA UFFICIO (form basato su immobile) ===
  const handleOpenNewTicket = () => {
    setShowNewTicket(true);
    setNewPropertySearch('');
    setNewSelectedProperty(null);
    setNewPropertyInfo(null);
    setNewTicketRecipients({ responsabile: false, proprieta: false, partner: false });
    setNewTicketForm({ subject: '', message: '', priority: 'normale', category: '' });
    setNewTicketAttachments([]);
  };

  const filteredProperties = useMemo(() => {
    if (!newPropertySearch.trim()) return [];
    const q = newPropertySearch.toLowerCase();
    return properties.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.property_code?.toLowerCase().includes(q)
    ).slice(0, 15);
  }, [properties, newPropertySearch]);

  const handleSelectProperty = async (prop) => {
    setNewSelectedProperty(prop);
    setNewPropertySearch('');
    setShowPropertyDropdown(false);
    setNewTicketRecipients({ responsabile: false, proprieta: false, partner: false });
    // Carica info dettagliate della proprietà
    try {
      const res = await fetch(`${API_URL}/api/tickets/property-info/${prop.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setNewPropertyInfo(await res.json());
      }
    } catch (e) {
      console.error('Errore caricamento info proprietà:', e);
    }
  };

  const handleNewTicketImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.slice(0, 5).forEach(file => {
      if (!file.type.startsWith('image/')) { toast.error('Solo immagini permesse'); return; }
      if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB per immagine'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewTicketAttachments(prev => [...prev.slice(0, 4), reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSendNewTicket = async () => {
    if (!newSelectedProperty) { toast.error('Seleziona un immobile'); return; }
    if (!newTicketForm.subject.trim() || !newTicketForm.message.trim()) { toast.error('Inserisci oggetto e messaggio'); return; }
    const selectedRecips = Object.entries(newTicketRecipients).filter(([, v]) => v).map(([k]) => k);
    if (selectedRecips.length === 0) { toast.error('Seleziona almeno un destinatario'); return; }

    setSendingNewTicket(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/from-office-property`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: newSelectedProperty.id,
          subject: newTicketForm.subject,
          message: newTicketForm.message,
          priority: newTicketForm.priority,
          category: newTicketForm.category || undefined,
          recipients: selectedRecips,
          attachments: newTicketAttachments
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowNewTicket(false);
        setPopup({ open: true, title: 'Ticket creato!', message: 'Il ticket è stato creato e inviato ai destinatari selezionati.', type: 'success' });
        fetchData();
      } else {
        setPopup({ open: true, title: 'Errore', message: data.detail || 'Errore nella creazione del ticket', type: 'error' });
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
    setSendingNewTicket(false);
  };

  const newTicketFileInputRef = useRef(null);

  // Close property dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(e.target)) {
        setShowPropertyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Filter and sort
  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    // Tab filter: aperti (non-chiuso) vs chiusi
    if (activeTab === 'aperti') {
      result = result.filter(t => t.status !== 'chiuso');
    } else {
      result = result.filter(t => t.status === 'chiuso');
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.subject?.toLowerCase().includes(term) ||
        t.property_name?.toLowerCase().includes(term) ||
        t.property_code?.toLowerCase().includes(term) ||
        t.created_by_name?.toLowerCase().includes(term)
      );
    }

    if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }
    if (filterPriority) {
      result = result.filter(t => t.priority === filterPriority);
    }
    if (filterRecipient) {
      result = result.filter(t => t.initial_recipient === filterRecipient);
    }
    if (filterProperty) {
      result = result.filter(t => t.property_id === filterProperty);
    }
    if (filterCategory) {
      result = result.filter(t => t.category === filterCategory);
    }
    if (filterResponsible) {
      result = result.filter(t => t.created_by_id === filterResponsible);
    }

    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle date fields
      if (sortConfig.key === 'created_at' || sortConfig.key === 'last_message_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle string fields (case insensitive)
      else if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [tickets, activeTab, searchTerm, filterStatus, filterPriority, filterRecipient, filterProperty, filterCategory, filterResponsible, sortConfig]);

  // Count active filters
  const activeFiltersCount = [filterStatus, filterPriority, filterRecipient, filterProperty, filterCategory, filterResponsible].filter(Boolean).length;

  // Count tickets per tab
  const openTicketsCount = useMemo(() => tickets.filter(t => t.status !== 'chiuso').length, [tickets]);
  const closedTicketsCount = useMemo(() => tickets.filter(t => t.status === 'chiuso').length, [tickets]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterRecipient('');
    setFilterProperty('');
    setFilterCategory('');
    setFilterResponsible('');
  };

  // Get category badge
  const getCategoryBadge = (category) => {
    const cat = TICKET_CATEGORIES[category];
    if (!cat) return null;
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-700',
      green: 'bg-green-100 text-green-700',
      purple: 'bg-purple-100 text-purple-700',
      gray: 'bg-gray-100 text-gray-600'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${colorClasses[cat.color] || colorClasses.gray}`}>
        {cat.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      aperto: { color: 'bg-green-100 text-green-700', icon: Clock, label: 'Aperto' },
      chiuso: { color: 'bg-slate-100 text-slate-600', icon: CheckCircle, label: 'Chiuso' },
      sospeso: { color: 'bg-yellow-100 text-yellow-700', icon: Pause, label: 'Sospeso' }
    };
    const badge = badges[status] || badges.aperto;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'urgente') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium animate-pulse">
          <AlertTriangle className="h-3 w-3" />
          URGENTE
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
        Normale
      </span>
    );
  };

  const getRecipientBadge = (recipient) => {
    const badges = {
      proprieta: { color: 'bg-green-50 text-green-700', icon: Building, label: 'Proprietario' },
      ufficio: { color: 'bg-purple-50 text-purple-700', icon: Users, label: 'Ufficio' },
      partner: { color: 'bg-orange-50 text-orange-700', icon: Building2, label: 'Partner' },
      entrambi: { color: 'bg-blue-50 text-blue-700', icon: Mail, label: 'Entrambi' }
    };
    const badge = badges[recipient] || badges.proprieta;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const getSenderStyle = (senderType) => {
    switch (senderType) {
      case 'responsabile':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'proprieta':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ufficio':
        return 'bg-purple-600 text-white';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getSenderLabel = (senderType) => {
    switch (senderType) {
      case 'responsabile': return 'Responsabile';
      case 'proprieta': return 'Proprietario';
      case 'ufficio': return 'Ufficio';
      default: return senderType;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SuccessPopup open={popup.open} onClose={() => setPopup(p => ({...p, open: false}))} title={popup.title} message={popup.message} type={popup.type} />
      <div className="space-y-6" data-testid="admin-tickets-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Ticket className="h-6 w-6 text-purple-600" />
              Gestione Ticket
            </h1>
            <p className="text-slate-500">Visualizza e gestisci tutte le comunicazioni</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/criticita">
              <Button variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Criticità Immobili
              </Button>
            </Link>
            <Button variant="outline" onClick={fetchModerationLogs}>
              <Shield className="h-4 w-4 mr-2" />
              Log Moderazione
            </Button>
            <Button onClick={handleOpenNewTicket} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-office-ticket-btn">
              <Send className="h-4 w-4 mr-2" />
              Nuovo Ticket
            </Button>
            <Button onClick={() => setShowBulkTicket(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="bulk-ticket-btn">
              <Users className="h-4 w-4 mr-2" />
              Ticket Multiplo
            </Button>
            <Button onClick={() => setShowBulkSends(true)} variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50" data-testid="bulk-sends-btn">
              <Archive className="h-4 w-4 mr-2" />
              Invii Multipli
            </Button>
          </div>
        </div>

        {/* Tabs Aperti/Chiusi */}
        <div className="flex border-b border-slate-200" data-testid="ticket-tabs">
          <button
            onClick={() => { setActiveTab('aperti'); setFilterStatus(''); }}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'aperti'
                ? 'border-purple-600 text-purple-700 bg-purple-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
            data-testid="tab-aperti"
          >
            Aperti / In Lavorazione
            {openTicketsCount > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'aperti' ? 'bg-purple-200 text-purple-800' : 'bg-slate-200 text-slate-600'
              }`}>
                {openTicketsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('chiusi'); setFilterStatus(''); }}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'chiusi'
                ? 'border-purple-600 text-purple-700 bg-purple-50/50'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
            data-testid="tab-chiusi"
          >
            <Archive className="h-4 w-4 inline mr-1" />
            Chiusi
            {closedTicketsCount > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'chiusi' ? 'bg-purple-200 text-purple-800' : 'bg-slate-200 text-slate-600'
              }`}>
                {closedTicketsCount}
              </span>
            )}
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-700">{stats.total || 0}</p>
              <p className="text-xs text-slate-500">Totale Ticket</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.by_status?.aperto || 0}</p>
              <p className="text-xs text-slate-500">Aperti</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.sospeso || 0}</p>
              <p className="text-xs text-slate-500">Sospesi</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-600">{stats.by_status?.chiuso || 0}</p>
              <p className="text-xs text-slate-500">Chiusi</p>
            </Card>
            <Card className={`p-4 text-center ${stats.urgent_open > 0 ? 'bg-red-50 border-red-200' : ''}`}>
              <p className={`text-2xl font-bold ${stats.urgent_open > 0 ? 'text-red-600 animate-pulse' : 'text-slate-600'}`}>
                {stats.urgent_open || 0}
              </p>
              <p className="text-xs text-slate-500">Urgenti Aperti</p>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca ticket..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-tickets"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
              data-testid="filter-status"
            >
              <option value="">Tutti gli stati</option>
              <option value="aperto">Aperti</option>
              <option value="sospeso">Sospesi</option>
              <option value="chiuso">Chiusi</option>
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
              data-testid="filter-priority"
            >
              <option value="">Tutte le priorità</option>
              <option value="urgente">Urgenti</option>
              <option value="normale">Normali</option>
            </select>
            <select
              value={filterRecipient}
              onChange={(e) => setFilterRecipient(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Tutti i destinatari</option>
              <option value="proprieta">Proprietario</option>
              <option value="ufficio">Ufficio</option>
              <option value="partner">Partner</option>
              <option value="entrambi">Entrambi</option>
            </select>
            <Button 
              variant={showFilters ? "default" : "outline"} 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
            >
              <Filter className="h-4 w-4 mr-1" />
              Filtri
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Filtri avanzati */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Immobile</label>
                <select
                  value={filterProperty}
                  onChange={(e) => setFilterProperty(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                  data-testid="filter-property"
                >
                  <option value="">Tutti gli immobili</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.property_code} - {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Categoria</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                  data-testid="filter-category"
                >
                  <option value="">Tutte le categorie</option>
                  {Object.entries(TICKET_CATEGORIES).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Responsabile</label>
                <select
                  value={filterResponsible}
                  onChange={(e) => setFilterResponsible(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm w-full"
                  data-testid="filter-responsible"
                >
                  <option value="">Tutti i responsabili</option>
                  {responsibles.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name || r.email}
                    </option>
                  ))}
                </select>
              </div>
              {activeFiltersCount > 0 && (
                <div className="md:col-span-3">
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Pulisci tutti i filtri ({activeFiltersCount})
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Table */}
        <Card className="overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b text-sm text-slate-600">
            {filteredTickets.length} ticket trovati
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('priority')} className="flex items-center gap-1 hover:text-blue-600">
                      Priorità {getSortIcon('priority')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-blue-600">
                      Data {getSortIcon('created_at')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('subject')} className="flex items-center gap-1 hover:text-blue-600">
                      Oggetto {getSortIcon('subject')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('property_name')} className="flex items-center gap-1 hover:text-blue-600">
                      Immobile {getSortIcon('property_name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Categoria</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('created_by_name')} className="flex items-center gap-1 hover:text-blue-600">
                      Responsabile {getSortIcon('created_by_name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-blue-600">
                      Stato {getSortIcon('status')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-slate-500">
                      Nessun ticket trovato
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors border-l-4 ${
                        ticket.status === 'chiuso'
                          ? 'bg-green-50/50 border-l-green-500'
                          : ticket.priority === 'urgente'
                            ? 'bg-red-50 border-l-red-600'
                            : 'bg-red-50/30 border-l-red-400'
                      } ${ticket.unread_by_office > 0 ? 'font-medium' : ''}`}
                      onClick={() => loadTicketDetail(ticket.id)}
                      data-testid={`ticket-row-${ticket.id}`}
                    >
                      <td className="px-4 py-3">{getPriorityBadge(ticket.priority)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(ticket.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800 truncate max-w-[200px]">{ticket.subject}</p>
                          {ticket.unread_by_office > 0 && (
                            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                              {ticket.unread_by_office}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-sm text-slate-600">
                          <Building2 className="h-3 w-3" />
                          {ticket.property_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getCategoryBadge(ticket.category)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-sm text-slate-600">
                          <User className="h-3 w-3" />
                          {ticket.created_by_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(ticket.status)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Detail Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTicket(null)}>
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail-modal">
              {loadingDetail ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="p-4 border-b bg-slate-50 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getStatusBadge(selectedTicket.status)}
                        {getPriorityBadge(selectedTicket.priority)}
                        {/* Show all recipients */}
                        {selectedTicket.recipients?.length > 0 ? (
                          selectedTicket.recipients.map(r => (
                            <span key={r}>{getRecipientBadge(r)}</span>
                          ))
                        ) : (
                          getRecipientBadge(selectedTicket.initial_recipient)
                        )}
                      </div>
                      <h2 className="text-lg font-bold text-slate-800">{selectedTicket.subject}</h2>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-4 w-4" />
                          {selectedTicket.property_name} ({selectedTicket.property_code})
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {selectedTicket.created_by_name}
                        </span>
                      </div>
                      {selectedTicket.owner_email && (
                        <p className="text-xs text-slate-400 mt-1">
                          Proprietario: {selectedTicket.owner_email}
                        </p>
                      )}
                      
                      {/* Tracking Info - Cronologia lettura/risposta */}
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-1">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-slate-500">Proprietario:</span>
                            {selectedTicket.first_read_by_owner_at ? (
                              <span className="ml-1 text-green-600">
                                Letto {new Date(selectedTicket.first_read_by_owner_at).toLocaleString('it-IT', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}
                              </span>
                            ) : (
                              <span className="ml-1 text-orange-500">Non ancora letto</span>
                            )}
                            {selectedTicket.first_reply_by_owner_at && (
                              <span className="ml-1 text-blue-600">
                                • Risposto {new Date(selectedTicket.first_reply_by_owner_at).toLocaleString('it-IT', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-slate-500">Ufficio:</span>
                            {selectedTicket.first_read_by_office_at ? (
                              <span className="ml-1 text-green-600">
                                Letto {new Date(selectedTicket.first_read_by_office_at).toLocaleString('it-IT', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}
                              </span>
                            ) : (
                              <span className="ml-1 text-orange-500">Non ancora letto</span>
                            )}
                            {selectedTicket.first_reply_by_office_at && (
                              <span className="ml-1 text-blue-600">
                                • Risposto {new Date(selectedTicket.first_reply_by_office_at).toLocaleString('it-IT', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)}>
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Timeline Events */}
                  {selectedTicket.timeline?.length > 0 && (
                    <div className="px-4 py-2 border-b bg-slate-50">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-slate-600 hover:text-slate-800 font-medium">
                          📋 Cronologia eventi ({selectedTicket.timeline.length})
                        </summary>
                        <div className="mt-2 space-y-1 pl-4 border-l-2 border-slate-200">
                          {selectedTicket.timeline.map((event, idx) => (
                            <div key={idx} className="text-slate-500">
                              <span className="font-medium">
                                {event.action === 'created' && 'Creato'}
                                {event.action === 'read_by_owner' && 'Letto da proprietario'}
                                {event.action === 'read_by_office' && 'Letto da ufficio'}
                                {event.action === 'reply_by_owner' && 'Risposta proprietario'}
                                {event.action === 'reply_by_office' && 'Risposta ufficio'}
                                {event.action === 'email_delivery' && 'Notifiche email'}
                              </span>
                              {event.action !== 'email_delivery' && (
                                <>
                                  <span className="ml-2">
                                    {event.actor_name || event.actor}
                                  </span>
                                  <span className="ml-2 text-slate-400">
                                    {new Date(event.timestamp).toLocaleString('it-IT')}
                                  </span>
                                </>
                              )}
                              {event.action === 'email_delivery' && event.delivery_log && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {event.triggered_by && (
                                    <span className="text-slate-400 text-xs block">Attivato da: {event.triggered_by} - {new Date(event.timestamp).toLocaleString('it-IT')}</span>
                                  )}
                                  {event.delivery_log.map((log, logIdx) => (
                                    <div key={logIdx} className="flex items-center gap-2">
                                      {log.status === 'inviata' && (
                                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-green-500 inline-block"></span>
                                          {log.name} ({log.email})
                                        </span>
                                      )}
                                      {log.status === 'errore' && (
                                        <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-red-500 inline-block"></span>
                                          {log.name} ({log.email}) - Errore: {log.error}
                                        </span>
                                      )}
                                      {log.status === 'non_inviata' && (
                                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                          <span className="h-2 w-2 rounded-full bg-amber-500 inline-block"></span>
                                          {log.name} - {log.error}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                    {ticketMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`rounded-lg p-3 border min-w-0 ${getSenderStyle(msg.sender_type)}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">
                            {getSenderLabel(msg.sender_type)}
                            {msg.sender_name && ` - ${msg.sender_name}`}
                          </span>
                          <span className="text-xs opacity-70">
                            {new Date(msg.sent_at).toLocaleString('it-IT')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                        {msg.recipients?.length > 0 && msg.sender_type === 'ufficio' && (
                          <p className="text-xs mt-2 opacity-70">
                            Inviato a: {msg.recipients.map(r => {
                              const labels = { responsabile: 'Responsabile', proprieta: 'Proprietario', partner: 'Partner' };
                              return labels[r] || r;
                            }).join(', ')}
                          </p>
                        )}
                        {msg.attachments?.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {msg.attachments.map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`Allegato ${idx + 1}`}
                                className="h-20 w-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => openAttachment(img)}
                                data-testid={`ticket-attachment-${idx}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Actions & Reply */}
                  {selectedTicket.status !== 'chiuso' && (
                    <div className="p-4 border-t bg-white space-y-3">
                      {/* Status buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <span className="text-sm text-slate-500 mr-2">Cambia stato:</span>
                        {selectedTicket.status !== 'aperto' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateStatus('aperto')}>
                            <Clock className="h-4 w-4 mr-1" /> Riapri
                          </Button>
                        )}
                        {selectedTicket.status !== 'sospeso' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateStatus('sospeso')}>
                            <Pause className="h-4 w-4 mr-1" /> Sospendi
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleUpdateStatus('chiuso')}>
                          <CheckCircle className="h-4 w-4 mr-1" /> Chiudi Ticket
                        </Button>
                      </div>

                      {/* Reply section */}
                      <div className="border-t pt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm text-slate-600">Invia a:</span>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={replyRecipients.includes('responsabile')}
                              onChange={() => toggleRecipient('responsabile')}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">Responsabile</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={replyRecipients.includes('proprieta')}
                              onChange={() => toggleRecipient('proprieta')}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">Proprietario</span>
                          </label>
                          {selectedTicket.partner_email && (
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={replyRecipients.includes('partner')}
                                onChange={() => toggleRecipient('partner')}
                                className="w-4 h-4 rounded"
                              />
                              <span className="text-sm">Partner</span>
                            </label>
                          )}
                        </div>

                        {replyAttachments.length > 0 && (
                          <div className="flex gap-2 mb-2 flex-wrap">
                            {replyAttachments.map((img, idx) => (
                              <div key={idx} className="relative">
                                <img src={img} alt="" className="h-14 w-14 object-cover rounded border" />
                                <button
                                  onClick={() => setReplyAttachments(prev => prev.filter((_, i) => i !== idx))}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            multiple
                            className="hidden"
                          />
                          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <Image className="h-4 w-4" />
                          </Button>
                          <Textarea
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="Scrivi una risposta..."
                            rows={4}
                            className="flex-1 min-h-[100px]"
                            data-testid="admin-reply-input"
                          />
                          <Button
                            onClick={handleSendReply}
                            disabled={!replyContent.trim() || sendingReply || replyRecipients.length === 0}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="admin-send-reply-btn"
                          >
                            {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedTicket.status === 'chiuso' && (
                    <div className="p-4 border-t bg-slate-50 text-center">
                      <p className="text-slate-500 text-sm">
                        Ticket chiuso il {new Date(selectedTicket.closed_at).toLocaleString('it-IT')}
                        {selectedTicket.closed_by && ` da ${selectedTicket.closed_by}`}
                      </p>
                      <Button size="sm" variant="outline" className="mt-2" onClick={() => handleUpdateStatus('aperto')}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Riapri Ticket
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}

        {/* Moderation Logs Modal */}
        {showModLogs && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModLogs(false)}>
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-red-600" />
                    Log Moderazione Ticket
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowModLogs(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {moderationLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessun messaggio bloccato</p>
                ) : (
                  <div className="space-y-3">
                    {moderationLogs.map((log) => (
                      <div key={log.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex gap-2">
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                              {log.content_type === 'image' ? 'Immagine' : 'Testo'}
                            </span>
                            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                              {log.sender_type === 'responsabile' ? 'Responsabile' : log.sender_type === 'proprieta' ? 'Proprietario' : 'Ufficio'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(log.checked_at).toLocaleString('it-IT')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mb-2">
                          {log.content_type === 'image' ? log.original_content : log.original_content?.substring(0, 200)}
                          {log.original_content?.length > 200 && '...'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {log.issues_found?.map((issue, idx) => (
                            <span key={idx} className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">
                              {issue}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Modale Nuovo Ticket da Ufficio (form basato su immobile) */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNewTicket(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-ticket-modal">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Send className="h-5 w-5 text-purple-600" />
                Nuovo Ticket
              </h2>
              <button onClick={() => setShowNewTicket(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Ricerca Immobile */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Immobile *</label>
                {newSelectedProperty ? (
                  <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800">{newSelectedProperty.name}</p>
                      <p className="text-xs text-slate-500">{newSelectedProperty.property_code}</p>
                    </div>
                    <button onClick={() => { setNewSelectedProperty(null); setNewPropertyInfo(null); setNewTicketRecipients({ responsabile: false, proprieta: false, partner: false }); }}
                      className="p-1 hover:bg-purple-100 rounded">
                      <X className="h-4 w-4 text-slate-400" />
                    </button>
                  </div>
                ) : (
                  <div className="relative" ref={propertyDropdownRef}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={newPropertySearch}
                        onChange={e => { setNewPropertySearch(e.target.value); setShowPropertyDropdown(true); }}
                        onFocus={() => setShowPropertyDropdown(true)}
                        placeholder="Cerca immobile per nome o codice..."
                        className="pl-10"
                        data-testid="new-ticket-property-search"
                      />
                    </div>
                    {showPropertyDropdown && newPropertySearch.length >= 2 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredProperties.length === 0 ? (
                          <div className="p-3 text-sm text-slate-400">Nessun immobile trovato</div>
                        ) : filteredProperties.map(p => (
                          <div key={p.id}
                            className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-purple-50 text-sm"
                            onClick={() => handleSelectProperty(p)}
                            data-testid={`property-option-${p.id}`}>
                            <Building2 className="h-4 w-4 text-slate-400" />
                            <div className="flex-1">
                              <div className="font-medium text-slate-800">{p.name}</div>
                              <div className="text-xs text-slate-400">{p.property_code}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Destinatari (visibili solo dopo aver selezionato immobile) */}
              {newSelectedProperty && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Destinatari *</label>
                  <div className="space-y-2">
                    {newPropertyInfo?.manager_email && (
                      <label className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="recipient-responsabile">
                        <input type="checkbox" checked={newTicketRecipients.responsabile}
                          onChange={e => setNewTicketRecipients(prev => ({ ...prev, responsabile: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-purple-600" />
                        <User className="h-4 w-4 text-blue-500" />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Responsabile</span>
                          <span className="text-xs text-slate-400 ml-2">{newPropertyInfo.manager_name || newPropertyInfo.manager_email}</span>
                        </div>
                      </label>
                    )}
                    {newPropertyInfo?.owner_email && (
                      <label className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="recipient-proprieta">
                        <input type="checkbox" checked={newTicketRecipients.proprieta}
                          onChange={e => setNewTicketRecipients(prev => ({ ...prev, proprieta: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-purple-600" />
                        <Building className="h-4 w-4 text-green-500" />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Proprietario</span>
                          <span className="text-xs text-slate-400 ml-2">{newPropertyInfo.owner_name || newPropertyInfo.owner_email}</span>
                        </div>
                      </label>
                    )}
                    {newPropertyInfo?.partner_email && (
                      <label className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid="recipient-partner">
                        <input type="checkbox" checked={newTicketRecipients.partner}
                          onChange={e => setNewTicketRecipients(prev => ({ ...prev, partner: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-purple-600" />
                        <Building2 className="h-4 w-4 text-orange-500" />
                        <div className="flex-1">
                          <span className="text-sm font-medium">Partner</span>
                          <span className="text-xs text-slate-400 ml-2">{newPropertyInfo.partner_name || newPropertyInfo.partner_email}</span>
                        </div>
                      </label>
                    )}
                    {newPropertyInfo && !newPropertyInfo.manager_email && !newPropertyInfo.owner_email && !newPropertyInfo.partner_email && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">Nessun destinatario configurato per questo immobile</p>
                    )}
                  </div>
                </div>
              )}

              {/* Oggetto */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Oggetto *</label>
                <Input value={newTicketForm.subject} onChange={e => setNewTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Es: Segnalazione guasto caldaia" data-testid="ticket-subject-input" />
              </div>

              {/* Priorità + Categoria */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Priorità</label>
                  <select value={newTicketForm.priority} onChange={e => setNewTicketForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm" data-testid="ticket-priority-select">
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Categoria</label>
                  <select value={newTicketForm.category} onChange={e => setNewTicketForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm" data-testid="ticket-category-select">
                    <option value="">Seleziona categoria</option>
                    {Object.entries(TICKET_CATEGORIES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Messaggio */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Messaggio *</label>
                <Textarea value={newTicketForm.message} onChange={e => setNewTicketForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Scrivi il messaggio..." rows={5} data-testid="ticket-message-textarea" />
              </div>

              {/* Allegati */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Allegati</label>
                <input type="file" ref={newTicketFileInputRef} onChange={handleNewTicketImageUpload} accept="image/*" multiple className="hidden" />
                {newTicketAttachments.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {newTicketAttachments.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt="" className="h-14 w-14 object-cover rounded border" />
                        <button onClick={() => setNewTicketAttachments(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={() => newTicketFileInputRef.current?.click()}>
                  <Image className="h-4 w-4 mr-1" /> Aggiungi foto
                </Button>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                {Object.values(newTicketRecipients).filter(Boolean).length} destinatari selezionati
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowNewTicket(false)}>Annulla</Button>
                <Button onClick={handleSendNewTicket} disabled={sendingNewTicket || !newSelectedProperty} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="send-ticket-btn">
                  {sendingNewTicket ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Invio...</> : <><Send className="h-4 w-4 mr-1" /> Invia Ticket</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <BulkTicketModal
        open={showBulkTicket}
        onClose={() => setShowBulkTicket(false)}
        categories={TICKET_CATEGORIES}
        onSent={(data) => {
          setPopup({ open: true, title: 'Ticket inviati!', message: data.message || `Creati ${data.tickets_created} ticket individuali.`, type: 'success' });
          fetchData();
        }}
      />
      <BulkSendsManager
        open={showBulkSends}
        onClose={() => setShowBulkSends(false)}
        onChanged={() => fetchData()}
      />
    </Layout>
  );
};

export default AdminTickets;
